import React from 'react';
import type {Location} from '../api/types';
import useLocationForecast from './useLocationForecast';

/** How often a shown forecast is checked for staleness (mobile's pace). */
const CHECK_MS = 40_000;

/**
 * Where the forecast of the shown place stands: `loading` until an answer
 * (and again while trying again), `ready` once fetched (or with nothing to
 * fetch), `failed` when the last request failed (`throttled` beside it says
 * whether it was refused for too many requests, FE #427).
 */
export type LocationForecastStatus = 'loading' | 'ready' | 'failed';

/**
 * Keeps a place's forecast fresh while its screen shows, in both apps
 * (mobile's Location app bar, the webapp's place page; FE #381 W4): asked
 * at once when stale (over 30 min, `isUpdateRequired`), then re-checked
 * every 40 s. `status` says where it stands for this place and model (a
 * failure for another one is forgotten); `retry` asks again.
 */
const useLocationForecastRefresh = (location: Location) => {
  const {updateLocationForecast, isUpdateRequired} =
    useLocationForecast(location);
  // Keyed on what is asked for: the place and the model it uses (the
  // update callback changes with the model).
  const [outcome, setOutcome] = React.useState<{
    for: unknown;
    status: LocationForecastStatus;
    throttled?: boolean;
  } | null>(null);
  const asking = React.useRef<unknown>(null);
  const mounted = React.useRef(true);
  React.useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const key = `${location?.hash}`;
  const update = React.useCallback(() => {
    const asked = {key, updateLocationForecast};
    asking.current = asked;
    if (mounted.current) {
      setOutcome({for: asked, status: 'loading'});
    }
    return updateLocationForecast().then((result) => {
      if (mounted.current && asking.current === asked) {
        setOutcome({
          for: asked,
          status:
            result === 'failed' || result === 'throttled' ? 'failed' : 'ready',
          throttled: result === 'throttled',
        });
      }
    });
  }, [key, updateLocationForecast]);

  React.useEffect(() => {
    if (isUpdateRequired()) {
      update();
    }
    const interval = setInterval(() => {
      if (isUpdateRequired()) {
        update();
      }
    }, CHECK_MS);
    return () => clearInterval(interval);
  }, [isUpdateRequired, update]);

  const current =
    outcome !== null &&
    (outcome.for as {key: string}).key === key &&
    (outcome.for as {updateLocationForecast: unknown})
      .updateLocationForecast === updateLocationForecast;
  // Nothing asked for this one yet: fresh data needs no request.
  const status: LocationForecastStatus = current
    ? outcome.status
    : isUpdateRequired()
      ? 'loading'
      : 'ready';

  // Only for the outcome shown: a retry's loading forgets it.
  const throttled = current && status === 'failed' && !!outcome.throttled;

  return {status, throttled, retry: update};
};

export default useLocationForecastRefresh;
